/**
 * Reset Password Page
 * 
 * Page for users to set a new password using the reset token from email.
 */

import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Card } from '../../components/ui';
import { authService } from '../../services';

type PageState = 'form' | 'success' | 'error';

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [pageState, setPageState] = useState<PageState>('form');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Password validation
  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return t('auth.register.errors.passwordLength');
    if (!/[A-Z]/.test(pwd)) return t('auth.register.errors.passwordUppercase');
    if (!/[a-z]/.test(pwd)) return t('auth.register.errors.passwordLowercase');
    if (!/[0-9]/.test(pwd)) return t('auth.register.errors.passwordDigit');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) return t('auth.register.errors.passwordSpecial');
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    if (!token) {
      setPageState('error');
      return;
    }
    
    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      setFormError(passwordError);
      return;
    }
    
    // Check passwords match
    if (password !== confirmPassword) {
      setFormError(t('auth.register.errors.passwordMismatch'));
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await authService.resetPassword(token, password);
      
      if (response.success) {
        setPageState('success');
      } else {
        // Check for specific error types
        if (response.error?.code === 'TOKEN_EXPIRED') {
          setFormError(t('auth.resetPassword.errors.tokenExpired'));
        } else {
          setFormError(response.error?.message || t('auth.resetPassword.errors.generic'));
        }
      }
    } catch {
      setFormError(t('auth.resetPassword.errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // No token provided
  if (!token) {
    return (
      <Card>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {t('auth.resetPassword.errors.invalidLink')}
          </h2>
          <p className="text-slate-500 mb-6">
            {t('auth.resetPassword.errors.invalidLinkMessage')}
          </p>
          <Link to="/auth/forgot-password">
            <Button variant="secondary">{t('auth.resetPassword.requestNewLink')}</Button>
          </Link>
        </div>
      </Card>
    );
  }

  // Error state
  if (pageState === 'error') {
    return (
      <Card>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {t('auth.resetPassword.errors.title')}
          </h2>
          <p className="text-slate-500 mb-6">
            {t('auth.resetPassword.errors.message')}
          </p>
          <Link to="/auth/forgot-password">
            <Button variant="secondary">{t('auth.resetPassword.requestNewLink')}</Button>
          </Link>
        </div>
      </Card>
    );
  }

  // Success state
  if (pageState === 'success') {
    return (
      <Card>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {t('auth.resetPassword.success.title')}
          </h2>
          <p className="text-slate-500 mb-6">
            {t('auth.resetPassword.success.message')}
          </p>
          <Button onClick={() => navigate('/auth/login')} className="w-full">
            {t('auth.resetPassword.success.login')}
          </Button>
        </div>
      </Card>
    );
  }

  // Form state
  return (
    <Card>
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-violet-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-slate-900">
          {t('auth.resetPassword.title')}
        </h1>
        <p className="text-slate-500 mt-2">
          {t('auth.resetPassword.subtitle')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t('auth.newPassword')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          required
          autoComplete="new-password"
          helperText={t('auth.register.passwordHint')}
          disabled={isSubmitting}
        />

        <Input
          label={t('auth.confirmPassword')}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="••••••••"
          required
          autoComplete="new-password"
          disabled={isSubmitting}
        />

        {formError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {formError}
          </div>
        )}

        <Button type="submit" className="w-full" isLoading={isSubmitting}>
          {t('auth.resetPassword.submit')}
        </Button>
      </form>

      <div className="mt-6 text-center text-sm text-slate-500">
        <Link to="/auth/login" className="text-violet-600 hover:text-violet-700 font-medium">
          {t('auth.forgotPassword.backToLogin')}
        </Link>
      </div>
    </Card>
  );
};

export default ResetPasswordPage;
